import {SimplifiedFeedbackGradeValue} from './types';

export type SimplifiedFeedbackCriterion = 'c2' | 'c4' | 'c6';
export type SimplifiedFeedbackSeal = 'mandou_bem' | 'pode_melhorar';

export interface SimplifiedFeedbackComment {
  seal: SimplifiedFeedbackSeal;
  title: string;
  comment: string;
}

function seal(grade: SimplifiedFeedbackGradeValue): SimplifiedFeedbackSeal {
  return grade <= 4 ? 'pode_melhorar' : 'mandou_bem';
}

// DRAFT (2026-09-21): interim copy, written to unblock demo/layout validation — Mari Parro
// (pedagogical) hasn't validated any of these 15 comments yet (see
// tarefas/devolutiva-simplificada-quinto-ano/context.md, "Deferred Ideas"). Swap for the final
// copy as soon as she delivers it; don't treat as final copy nor show it to real users outside
// internal test/demo.
const SIMPLIFIED_FEEDBACK_COMMENTS: Record<
  SimplifiedFeedbackCriterion,
  Record<SimplifiedFeedbackGradeValue, SimplifiedFeedbackComment>
> = {
  c2: {
    2: {
      seal: seal(2),
      title: 'Assunto',
      comment:
        'Seu texto ainda não conta bem sobre o assunto pedido. Releia a proposta com calma antes de escrever de novo.',
    },
    4: {
      seal: seal(4),
      title: 'Assunto',
      comment:
        'Seu texto toca no assunto, mas ainda foge dele em alguns pontos. Vale reler a proposta.',
    },
    6: {
      seal: seal(6),
      title: 'Assunto',
      comment:
        'Seu texto fala do assunto pedido na maior parte do tempo. Continue prestando atenção pra não fugir do tema.',
    },
    8: {
      seal: seal(8),
      title: 'Assunto',
      comment:
        'Seu texto segue bem o assunto pedido, com poucos deslizes. Muito bem!',
    },
    10: {
      seal: seal(10),
      title: 'Assunto',
      comment:
        'Seu texto atende muito bem ao assunto pedido, do início ao fim. Excelente!',
    },
  },
  c4: {
    2: {
      seal: seal(2),
      title: 'Formato',
      comment:
        'Seu texto ainda não tem a estrutura esperada para esse tipo de texto. Vale estudar como ele costuma ser organizado.',
    },
    4: {
      seal: seal(4),
      title: 'Formato',
      comment:
        'Seu texto está começando a seguir a estrutura esperada, mas ainda falta organizar melhor as partes.',
    },
    6: {
      seal: seal(6),
      title: 'Formato',
      comment:
        'Seu texto segue boa parte da estrutura esperada para esse gênero. Continue assim!',
    },
    8: {
      seal: seal(8),
      title: 'Formato',
      comment:
        'Seu texto está bem organizado, seguindo quase toda a estrutura esperada.',
    },
    10: {
      seal: seal(10),
      title: 'Formato',
      comment:
        'Seu texto segue perfeitamente a estrutura esperada para esse gênero. Show!',
    },
  },
  c6: {
    2: {
      seal: seal(2),
      title: 'Ideias',
      comment:
        'As ideias do seu texto ainda estão um pouco soltas. Tente ligar melhor uma ideia à outra.',
    },
    4: {
      seal: seal(4),
      title: 'Ideias',
      comment:
        'Algumas ideias do seu texto já se conectam, mas outras ainda ficam soltas. Continue treinando.',
    },
    6: {
      seal: seal(6),
      title: 'Ideias',
      comment:
        'As ideias do seu texto se conectam bem na maior parte do tempo. Bom trabalho!',
    },
    8: {
      seal: seal(8),
      title: 'Ideias',
      comment:
        'As ideias do seu texto estão bem conectadas e fazem sentido juntas.',
    },
    10: {
      seal: seal(10),
      title: 'Ideias',
      comment:
        'As ideias do seu texto estão muito bem conectadas, formando um raciocínio claro do início ao fim!',
    },
  },
};

export function getSimplifiedFeedbackComment(
  criterion: SimplifiedFeedbackCriterion,
  grade: number | null | undefined,
): SimplifiedFeedbackComment | null {
  if (grade == null) return null;

  return (
    SIMPLIFIED_FEEDBACK_COMMENTS[criterion]?.[
      grade as SimplifiedFeedbackGradeValue
    ] ?? null
  );
}
